import type { RaceDetails, Acceptor, Race } from '@mobi/betslip/types'
import { type MarketDataTransferObject, PlanSeq } from '@mobi/api-types'
import type {
  RacePageDataTransferObject,
  ResultsPageDataTransferObject,
} from '@classic/Betting-v2/DataTransferObjects/DataTransferObjects'
import { LoadBetErrors } from '@core/Areas/ShareBet/LoadBet/constants'
import { getRacesPageDataFromApi } from '@core/Areas/Racing/api'
import { formatFixtureDateToSimpleString } from '@core/Areas/Racing/helpers/formatFixtureDateToSimpleString'

export const getSelectionDetails = async (
  fixtureId: string,
  fixtureDate: string,
  raceNumbers: number[],
  selections: string[][],
  planSeq: PlanSeq,
  shouldForceWinOnly = false
): Promise<{
  selectionDetails: RaceDetails
  shouldAllowPlaceInvestment: boolean
  fixedOddsMarkets?: MarketDataTransferObject[]
}> => {
  const isMultiRace = raceNumbers.length > 1
  const isWinPlaceDetails = isWinPlace(planSeq)

  const formattedDate = formatFixtureDateToSimpleString(fixtureDate)
  const raceArray = isWinPlaceDetails ? [raceNumbers[0]] : raceNumbers
  const meeting = await getRacesPageDataFromApi({
    fixtureDate: formattedDate,
    fixtureId,
    raceNumbers: raceArray,
  })

  let shouldAllowPlaceInvestment = false
  if (isWinPlaceDetails) {
    const { HasPlacePool, FixedOddsInfo } = meeting.MeetingInformation.SelectedRace
    shouldAllowPlaceInvestment =
      !shouldForceWinOnly && !!(HasPlacePool || FixedOddsInfo?.IsPlaceAvailable)
  }

  if (isBettingClosedOnRace(meeting)) throw new Error(LoadBetErrors.BettingClosed)

  const { MeetingInformation: meetingInformation, RaceStarters: raceStarters } = meeting

  const races = meetingInformation.Races.filter(race => raceNumbers.includes(race.RaceNumber)).map(
    (raceDetails, index) => {
      const acceptorKeysList = isMultiRace
        ? selections[index]
        : selections.flatMap(selection => selection)

      const race: Race = {
        key: raceDetails.Key,
        raceNumber: raceDetails.RaceNumber,
        meetingName: meetingInformation.MeetingName,
        meetingCode: meetingInformation.MeetingCode,
        meetingId: meetingInformation.MeetingId,
        meetingDate: meetingInformation.MeetingDate,
        distance: raceDetails.Distance,
        raceTime: raceDetails.RaceTime,
        type: raceDetails.RaceType,
        isFixedOddsRace: raceDetails.IsFixedOddsRace,
        // Either Split the keys across races, or all on single race
        acceptorKeys: acceptorKeysList.map(acceptor =>
          createAcceptorKey(raceDetails.RaceNumber, acceptor)
        ),
      }

      return race
    }
  )

  // Flatten acceptors across all races
  const acceptors = raceStarters
    .filter(race => raceNumbers.includes(race.RaceKey.RaceNumber))
    .flatMap(race => {
      return race.Starters.map(starter => {
        const acceptor: Acceptor = {
          key: createAcceptorKey(race.RaceKey.RaceNumber, starter.Number),
          name: starter.Name,
          number: starter.Number,
          type: starter.Type,
          imageUrl: starter.SilkImages.small.url,
          imageUrlWithSizes: starter.SilkImages,
          meetingName: meetingInformation.MeetingName,
          isScratched: starter.IsScratched,
          scratchType: starter.ScratchType,
          fixedOdds: {
            win: starter.FixedOddsStarterInfo?.DisplayWinDividend,
            place: starter.FixedOddsStarterInfo?.DisplayPlaceDividend,
            isSuspended: starter.FixedOddsStarterInfo?.IsSuspended,
          },
          toteOdds: {
            win: starter.DisplayWinDividend,
            place: starter.DisplayPlaceDividend,
          },
        }

        return acceptor
      })
    })

  return {
    selectionDetails: {
      races,
      acceptors,
    },
    fixedOddsMarkets: meeting.FixedOddsMarkets,
    shouldAllowPlaceInvestment,
  }
}

// =============
// Local Helpers
// =============

function createAcceptorKey(raceNumber: number, selection: string | number): string {
  return `${raceNumber}-${selection}`
}

function isWinPlace(planSeq: number): boolean {
  if ([PlanSeq.Win, PlanSeq.Place, PlanSeq.WinAndPlace, PlanSeq.FOBRacing].includes(planSeq))
    return true
  return false
}

function isBettingClosedOnRace(
  meeting: RacePageDataTransferObject | ResultsPageDataTransferObject
): meeting is ResultsPageDataTransferObject {
  return meeting.Tag === 'Results'
}
